import { c as _c } from "react/compiler-runtime";
import Logo from '@/assets/logos/logo_header.svg';
import { LINKS, SELF_CARE_LINKS } from '@/constants/links';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { useRef, startTransition, useCallback, useMemo, memo } from 'react';
import { mergeClasses } from '@/utils/style';
import { Button } from '@/components/buttons';
import { Link } from '@/components/links';
import { Section, useSections } from './useSections';
import { Subsection } from './Subsection';
import MenuIcon from '@/assets/icons/heroIcons/Menu.svg';
import CrossIcon from '@/assets/icons/heroIcons/Cross.svg';
import ChevronDownIcon from '@/assets/icons/heroIcons/Chevron-down.svg';
import UserCircleIcon from '@/assets/icons/heroIcons/User-circle.svg';
import { normalizePath, uppercaseFirstLetter } from '@/utils/string';
import { motion } from 'framer-motion';
import { hasWindow } from '@/utils/dom';
import { ClientGate } from '@/components/ClientGate';
import { VariantProps, useHeader } from './useHeader';
import { SetState } from '@/utils/types';
import { PageMenuPromoBanner } from '@/api/sanity/pageMenuPromoBanner';
const MENU_WIDTH = 440;
const variants = {
  open: ({
    windowWidth,
    isResponsive
  }: VariantProps) => ({
    x: isResponsive ? 0 : windowWidth >= MENU_WIDTH ? windowWidth - MENU_WIDTH : 0,
    transition: {
      type: 'spring' as const,
      ease: 'easeIn' as const,
      bounce: 0,
      duration: 0.3
    }
  }),
  closed: ({
    windowWidth,
    isResponsive
  }: VariantProps) => ({
    x: isResponsive ? 0 : windowWidth >= MENU_WIDTH ? windowWidth + MENU_WIDTH : '100%',
    transition: {
      type: 'spring' as const,
      ease: 'easeIn' as const,
      bounce: 0,
      duration: 0.3
    }
  })
};
interface MobileControlProps {
  onClose: () => void;
}
const navButtonsDimensions = 'h-10 2xl:h-12 4xl:h-14 font-medium!';
export const navLinks = 'py-3 xl:py-6 3xl:py-6 4xl:py-9';
function MobileControl(props) {
  const $ = _c(8);
  const {
    t
  } = useTranslation();
  const {
    onClose
  } = props;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = LINKS();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Link to={t0.homepage} analyticsParam="headerMobileHome" testId="headerMobileHome" className="text-gray-900 xl:hidden"><Logo className="h-10 w-auto text-base" /></Link>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t) {
    t2 = t("header.mobile.closeMenu");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <CrossIcon />;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== onClose || $[6] !== t2) {
    t4 = <div className="z-30 mb-11 ml-[3px] flex justify-between xl:mb-0 xl:ml-0 xl:hidden">{t1}<Button className="text-gray-900" variant="unstyled" analyticsParam="mobileMenuClose" testId="mobileMenuClose" onClick={onClose} ariaLabel={t2}>{t3}</Button></div>;
    $[5] = onClose;
    $[6] = t2;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}
function MobileButtons(t0) {
  const $ = _c(3);
  const {
    isLoggedIn
  } = t0;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[0] !== isLoggedIn || $[1] !== t) {
    t1 = <div className="shadow-floating fixed bottom-0 left-0 flex w-full flex-col items-center justify-center gap-2 rounded-t-2xl bg-white p-6 xl:hidden">{isLoggedIn ? <Button variant="primary" analyticsParam="selfCareOverview" testId="selfCareOverview" to={SELF_CARE_LINKS.overview} className="w-full justify-center! lg:w-full" leftIcon={<UserCircleIcon className="-my-2 h-7 w-7" />}>{t("header.loggedIn.button")}</Button> : <><Button variant="primary" analyticsParam="headerRegister" testId="headerRegister" to={LINKS().registration} className="w-full lg:w-full">{t("header.register.mobile.inner.button")}</Button><Button variant="secondary" analyticsParam="headerLogin" testId="headerLogin" to={LINKS().login} className="w-full lg:w-full">{t("header.login.button")}</Button></>}</div>;
    $[0] = isLoggedIn;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function MobileEllipse() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="absolute right-[-160px] -z-10 flex h-64 w-64 rotate-[-50deg] rounded-full bg-linear-to-t from-blue-200 from-10% to-transparent to-80% xl:hidden" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function ClientNavFallback() {
  const $ = _c(4);
  const sections = useSections();
  let t0;
  if ($[0] !== sections) {
    t0 = sections.map(_temp2);
    $[0] = sections;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = <nav className="h-10 w-10 opacity-0 lg:h-18 lg:w-18">{t0}</nav>;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function _temp2(section) {
  return <ul key={section.id}><li>{section.subsection ? <><h4>{section.name}</h4><ul>{section.subsection.map(_temp)}</ul></> : <Link analyticsParam={`header${uppercaseFirstLetter(section.id)}`} testId={`header${uppercaseFirstLetter(section.id)}`} to={section.path}><h3>{section.name}</h3></Link>}</li></ul>;
}
function _temp(subsection) {
  return <li key={subsection.id}><Link to={subsection.path} analyticsParam={`headerSubmenu${uppercaseFirstLetter(subsection.id)}`} testId={`headerSubmenu${uppercaseFirstLetter(subsection.id)}`} aria-label={subsection.name}><div><h5>{subsection.name}</h5><div>{subsection.description}</div></div></Link></li>;
}
interface SectionItemProps {
  handleSubsectionOpen: (sectionId: Section['id'], options?: {
    immediate?: boolean;
  }) => void;
  handleSubsectionClose: () => void;
  debouncedOpenedSubmenu: string | null;
  isOpenMobileMenu: boolean;
  scrolling: boolean;
  enforceBackground: boolean;
  isSectionActive: (section: Section) => boolean;
  variant: 'default' | 'inverted' | undefined;
  section: Section;
  setOpenedSubmenu: SetState<string | null>;
  setDebouncedOpenedSubmenu: SetState<string | null>;
  setIsOpenMobileMenu: (value: boolean) => void;
}
const SectionItem = memo(function SectionItem(props) {
  const $ = _c(22);
  const {
    section,
    handleSubsectionOpen,
    setDebouncedOpenedSubmenu,
    setOpenedSubmenu,
    handleSubsectionClose,
    setIsOpenMobileMenu,
    debouncedOpenedSubmenu,
    isOpenMobileMenu,
    scrolling,
    enforceBackground,
    isSectionActive,
    variant
  } = props;
  let t0;
  if ($[0] !== handleSubsectionOpen || $[1] !== section.id) {
    t0 = ev => {
      const target = ev.target as HTMLElement;
      if (target.classList.contains("menu-polygon")) {
        return;
      }
      if (ev.target instanceof SVGPathElement) {
        return;
      }
      handleSubsectionOpen(section.id);
    };
    $[0] = handleSubsectionOpen;
    $[1] = section.id;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const onHover = t0;
  let t1;
  if ($[3] !== handleSubsectionOpen || $[4] !== section.id) {
    t1 = () => handleSubsectionOpen(section.id, {
      immediate: true
    });
    $[3] = handleSubsectionOpen;
    $[4] = section.id;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== debouncedOpenedSubmenu || $[7] !== enforceBackground || $[8] !== isOpenMobileMenu || $[9] !== isSectionActive || $[10] !== scrolling || $[11] !== section || $[12] !== setDebouncedOpenedSubmenu || $[13] !== setIsOpenMobileMenu || $[14] !== setOpenedSubmenu || $[15] !== variant) {
    t2 = "subsection" in section && section.subsection ? <Subsection isOpen={debouncedOpenedSubmenu === section.id} handleClick={() => {
      startTransition(() => {
        setOpenedSubmenu(value => {
          if (value === section.id) {
            return null;
          }
          return section.id;
        });
        if (isOpenMobileMenu) {
          setDebouncedOpenedSubmenu(value_0 => {
            if (value_0 === section.id) {
              return null;
            }
            return section.id;
          });
        }
      });
    }} sectionName={section.name} sectionId={section.id} key={section.id} subsection={section.subsection} promo={section.promo} isScrolling={scrolling || enforceBackground} isSectionActive={isSectionActive(section)} variant={variant} /> : "path" in section ? <Link analyticsParam={`header${uppercaseFirstLetter(section.id)}`} testId={`header${uppercaseFirstLetter(section.id)}`} to={section.path} className={mergeClasses("flex justify-between text-gray-900 no-underline", navLinks, {
      "xl:text-white!": variant === "inverted" && !scrolling && !enforceBackground
    })} onClick={() => setIsOpenMobileMenu(false)}><h3 className={mergeClasses("text-lg font-bold whitespace-nowrap sm:font-semibold xl:text-[15px] xl:font-normal 2xl:text-base", isSectionActive(section) && "font-bold sm:font-semibold xl:text-blue-500")}>{section.name}</h3><ChevronDownIcon className="block rotate-[270deg] xl:hidden" /></Link> : null;
    $[6] = debouncedOpenedSubmenu;
    $[7] = enforceBackground;
    $[8] = isOpenMobileMenu;
    $[9] = isSectionActive;
    $[10] = scrolling;
    $[11] = section;
    $[12] = setDebouncedOpenedSubmenu;
    $[13] = setIsOpenMobileMenu;
    $[14] = setOpenedSubmenu;
    $[15] = variant;
    $[16] = t2;
  } else {
    t2 = $[16];
  }
  let t3;
  if ($[17] !== handleSubsectionClose || $[18] !== onHover || $[19] !== t1 || $[20] !== t2) {
    t3 = <ul><li onMouseEnter={onHover} onMouseLeave={handleSubsectionClose} onFocus={t1} onBlur={handleSubsectionClose}>{t2}</li></ul>;
    $[17] = handleSubsectionClose;
    $[18] = onHover;
    $[19] = t1;
    $[20] = t2;
    $[21] = t3;
  } else {
    t3 = $[21];
  }
  return t3;
});
interface HeaderProps {
  variant?: 'default' | 'inverted';
  enforceBackground?: boolean;
  menuPromoBanners?: PageMenuPromoBanner[];
}
export function Header(t0) {
  const $ = _c(86);
  const {
    variant,
    enforceBackground: t1,
    menuPromoBanners: t2
  } = t0;
  const enforceBackground = t1 === undefined ? false : t1;
  let t3;
  if ($[0] !== t2) {
    t3 = t2 === undefined ? [] : t2;
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const menuPromoBanners = t3;
  const ref = useRef(null);
  const router = useRouter();
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      headerRef: ref
    };
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const {
    isInApp,
    debouncedOpenedSubmenu,
    isOpenMobileMenu,
    isResponsive,
    isLoggedIn,
    scrolling,
    handleSubsectionOpen,
    handleSubsectionClose,
    setIsOpenMobileMenu,
    setOpenedSubmenu,
    setDebouncedOpenedSubmenu,
    toggleLockScroll
  } = useHeader(t4);
  let t5;
  if ($[3] !== router.pathname) {
    t5 = section => {
      if ("path" in section) {
        return router.pathname === normalizePath(section.path);
      }
      return section.subsection.some(subsection => router.pathname === normalizePath(subsection.path));
    };
    $[3] = router.pathname;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const isSectionActive = t5;
  const sections = useSections();
  const {
    t
  } = useTranslation();
  let t6;
  if ($[5] !== menuPromoBanners || $[6] !== sections) {
    let t7;
    if ($[8] !== menuPromoBanners) {
      t7 = section_0 => ({
        ...section_0,
        promo: menuPromoBanners.find(promo => promo.sectionId === section_0.id)
      });
      $[8] = menuPromoBanners;
      $[9] = t7;
    } else {
      t7 = $[9];
    }
    t6 = sections.map(t7);
    $[5] = menuPromoBanners;
    $[6] = sections;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const updatedSections = t6;
  if (isInApp) {
    return null;
  }
  const t7 = scrolling || enforceBackground ? "shadow-floating bg-white" : "bg-transparent";
  let t8;
  if ($[10] !== t7) {
    t8 = mergeClasses("4xl:gap-16 fixed z-50 mx-auto flex max-w-[1792px] items-center gap-5 sm:gap-6 2xl:gap-4", "transition-all duration-300", "4xl:rounded-2xl 4xl:pl-4 right-0 left-0 py-2.5 pr-4 pl-1.5 sm:py-3 xl:top-4 xl:right-4 xl:left-4 xl:rounded-xl xl:py-0", t7);
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = LINKS();
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const t10 = t9.homepage;
  const t11 = variant === "inverted" && !scrolling && !enforceBackground ? "text-white!" : "text-gray-900!";
  let t12;
  if ($[13] !== t11) {
    t12 = mergeClasses("transition-colors duration-200", t11);
    $[13] = t11;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  let t13;
  if ($[15] !== t) {
    t13 = t("link.homepage");
    $[15] = t;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Logo className="4xl:h-14 -mr-3 h-10 sm:mr-0 xl:h-9 2xl:h-[2.6375rem]" />;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  let t15;
  if ($[18] !== t12 || $[19] !== t13) {
    t15 = <Link to={t10} analyticsParam="headerHome" testId="headerHome" className={t12} aria-label={t13}>{t14}</Link>;
    $[18] = t12;
    $[19] = t13;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  let t16;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <ClientNavFallback />;
    $[21] = t16;
  } else {
    t16 = $[21];
  }
  const t17 = isOpenMobileMenu && "fixed top-0 right-0 bottom-0 left-0 z-50 flex h-full max-w-[27.5rem] flex-col bg-blue-100 p-4 pt-[10px]";
  const t18 = isResponsive ? "block" : isOpenMobileMenu ? "block" : "hidden";
  let t19;
  if ($[22] !== t17 || $[23] !== t18) {
    t19 = mergeClasses("3xl:gap-5 4xl:gap-8 hidden gap-4 overflow-hidden xl:flex xl:transform-none! xl:overflow-visible", t17, t18);
    $[22] = t17;
    $[23] = t18;
    $[24] = t19;
  } else {
    t19 = $[24];
  }
  let t20;
  if ($[25] !== isResponsive) {
    t20 = {
      windowWidth: hasWindow() ? window.innerWidth : 0,
      isResponsive
    };
    $[25] = isResponsive;
    $[26] = t20;
  } else {
    t20 = $[26];
  }
  const t21 = t20 satisfies VariantProps;
  const t22 = isResponsive ? "open" : isOpenMobileMenu ? "open" : "closed";
  let t23;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <MobileEllipse />;
    $[27] = t23;
  } else {
    t23 = $[27];
  }
  let t24;
  if ($[28] !== setIsOpenMobileMenu) {
    t24 = <MobileControl onClose={() => setIsOpenMobileMenu(false)} />;
    $[28] = setIsOpenMobileMenu;
    $[29] = t24;
  } else {
    t24 = $[29];
  }
  let t25;
  if ($[30] !== debouncedOpenedSubmenu || $[31] !== enforceBackground || $[32] !== handleSubsectionClose || $[33] !== handleSubsectionOpen || $[34] !== isOpenMobileMenu || $[35] !== isSectionActive || $[36] !== scrolling || $[37] !== setDebouncedOpenedSubmenu || $[38] !== setIsOpenMobileMenu || $[39] !== setOpenedSubmenu || $[40] !== updatedSections || $[41] !== variant) {
    let t26;
    if ($[43] !== debouncedOpenedSubmenu || $[44] !== enforceBackground || $[45] !== handleSubsectionClose || $[46] !== handleSubsectionOpen || $[47] !== isOpenMobileMenu || $[48] !== isSectionActive || $[49] !== scrolling || $[50] !== setDebouncedOpenedSubmenu || $[51] !== setIsOpenMobileMenu || $[52] !== setOpenedSubmenu || $[53] !== variant) {
      t26 = section_1 => <SectionItem debouncedOpenedSubmenu={debouncedOpenedSubmenu} enforceBackground={enforceBackground} handleSubsectionClose={handleSubsectionClose} handleSubsectionOpen={handleSubsectionOpen} isOpenMobileMenu={isOpenMobileMenu} isSectionActive={isSectionActive} scrolling={scrolling} section={section_1} setDebouncedOpenedSubmenu={setDebouncedOpenedSubmenu} setIsOpenMobileMenu={setIsOpenMobileMenu} setOpenedSubmenu={setOpenedSubmenu} variant={variant} key={section_1.id} />;
      $[43] = debouncedOpenedSubmenu;
      $[44] = enforceBackground;
      $[45] = handleSubsectionClose;
      $[46] = handleSubsectionOpen;
      $[47] = isOpenMobileMenu;
      $[48] = isSectionActive;
      $[49] = scrolling;
      $[50] = setDebouncedOpenedSubmenu;
      $[51] = setIsOpenMobileMenu;
      $[52] = setOpenedSubmenu;
      $[53] = variant;
      $[54] = t26;
    } else {
      t26 = $[54];
    }
    t25 = updatedSections.map(t26);
    $[30] = debouncedOpenedSubmenu;
    $[31] = enforceBackground;
    $[32] = handleSubsectionClose;
    $[33] = handleSubsectionOpen;
    $[34] = isOpenMobileMenu;
    $[35] = isSectionActive;
    $[36] = scrolling;
    $[37] = setDebouncedOpenedSubmenu;
    $[38] = setIsOpenMobileMenu;
    $[39] = setOpenedSubmenu;
    $[40] = updatedSections;
    $[41] = variant;
    $[42] = t25;
  } else {
    t25 = $[42];
  }
  let t26;
  if ($[55] !== t25) {
    t26 = <div className="4xl:gap-8 flex h-[calc(100%-14rem)] flex-col overflow-x-auto xl:flex-row xl:gap-5 xl:overflow-visible xl:pb-0 2xl:gap-6">{t25}</div>;
    $[55] = t25;
    $[56] = t26;
  } else {
    t26 = $[56];
  }
  let t27;
  if ($[57] !== isLoggedIn) {
    t27 = <MobileButtons isLoggedIn={isLoggedIn} />;
    $[57] = isLoggedIn;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  let t28;
  if ($[59] !== t19 || $[60] !== t21 || $[61] !== t22 || $[62] !== t24 || $[63] !== t26 || $[64] !== t27 || $[65] !== toggleLockScroll) {
    t28 = <ClientGate fallback={t16}><motion.nav className={t19} variants={variants} custom={t21} animate={t22} initial="closed" onAnimationComplete={toggleLockScroll}>{t23}{t24}{t26}{t27}</motion.nav></ClientGate>;
    $[59] = t19;
    $[60] = t21;
    $[61] = t22;
    $[62] = t24;
    $[63] = t26;
    $[64] = t27;
    $[65] = toggleLockScroll;
    $[66] = t28;
  } else {
    t28 = $[66];
  }
  let t29;
  let t30;
  if ($[67] !== isLoggedIn || $[68] !== t) {
    t29 = <div className="ml-auto hidden items-center space-x-3 xl:flex">{isLoggedIn ? <Button variant="primary" analyticsParam="selfCareOverview" testId="selfCareOverview" to={SELF_CARE_LINKS.overview} className={mergeClasses("w-[15.5rem]!", navButtonsDimensions)} leftIcon={<UserCircleIcon className="-my-2 h-7 w-7" />}>{t("header.loggedIn.button")}</Button> : <><Button variant="secondary" analyticsParam="headerLogin" testId="headerLogin" to={LINKS().login} className={mergeClasses("3xl:w-50 xl:w-32 2xl:w-36", navButtonsDimensions)}>{t("header.login.button")}</Button><Button variant="primary" analyticsParam="headerRegister" testId="headerRegister" to={LINKS().registration} className={mergeClasses("3xl:w-50 xl:w-32 2xl:w-36", navButtonsDimensions)}>{t("header.register.button")}</Button></>}</div>;
    t30 = isLoggedIn ? <Button className={mergeClasses("ml-auto flex w-32 justify-center px-4 py-2 min-[23rem]:w-auto! xl:hidden", navButtonsDimensions)} variant="primary" analyticsParam="headerMobileSelfCareOverview" testId="headerMobileSelfCareOverview" to={SELF_CARE_LINKS.overview} leftIcon={<UserCircleIcon className="-my-2 h-7 w-7" />}><span className="w-full max-w-full truncate">{t("header.loggedIn.mobile.button")}</span></Button> : <Button className={mergeClasses("ml-auto flex w-32 justify-center px-4 py-2 xl:hidden", navButtonsDimensions)} variant="primary" analyticsParam="headerMobileRegister" testId="headerMobileRegister" to={LINKS().registration}>{t("header.register.mobile.button")}</Button>;
    $[67] = isLoggedIn;
    $[68] = t;
    $[69] = t29;
    $[70] = t30;
  } else {
    t29 = $[69];
    t30 = $[70];
  }
  let t31;
  if ($[71] !== setIsOpenMobileMenu) {
    t31 = () => setIsOpenMobileMenu(true);
    $[71] = setIsOpenMobileMenu;
    $[72] = t31;
  } else {
    t31 = $[72];
  }
  let t32;
  if ($[73] !== t) {
    t32 = t("header.mobile.openMenu");
    $[73] = t;
    $[74] = t32;
  } else {
    t32 = $[74];
  }
  let t33;
  if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <MenuIcon />;
    $[75] = t33;
  } else {
    t33 = $[75];
  }
  let t34;
  if ($[76] !== t31 || $[77] !== t32) {
    t34 = <Button className="flex text-blue-500 xl:hidden" variant="unstyled" analyticsParam="mobileMenuOpen" testId="mobileMenuOpen" onClick={t31} ariaLabel={t32}>{t33}</Button>;
    $[76] = t31;
    $[77] = t32;
    $[78] = t34;
  } else {
    t34 = $[78];
  }
  let t35;
  if ($[79] !== t15 || $[80] !== t28 || $[81] !== t29 || $[82] !== t30 || $[83] !== t34 || $[84] !== t8) {
    t35 = <header ref={ref} className={t8}>{t15}{t28}{t29}{t30}{t34}</header>;
    $[79] = t15;
    $[80] = t28;
    $[81] = t29;
    $[82] = t30;
    $[83] = t34;
    $[84] = t8;
    $[85] = t35;
  } else {
    t35 = $[85];
  }
  return t35;
}